import { Icon } from "./widgets/Icon"
import { useQuery } from "@tanstack/react-query"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import clsx from "clsx"

export function DynamicIcon(props: {
  icon: string,
  color?: string,
  size?: string,
  className?: string,
  'aria-label'?: string,
  prefixFallback?: "fa-" | "radix-" | "mui-" | "lucide-"
}) {
  const loadedIcon = useQuery<React.ReactNode>({
    queryKey: [ "icon", props.icon, props.size, props.color ],
    queryFn: async () => {
      const style = { fontSize: props.size, color: props.color }
      if (props.icon.startsWith("fa-")) {
        return <Icon className={props.className}
                     icon={props.icon as IconProp}
                     style={style}
                     aria-label={props['aria-label'] ?? `${props.icon} icon`}
                     data-testid={props.icon} />
      }

      if (props.icon.startsWith("lucide-")) {
        const importName = kebabToCamelCase(props.icon.replace(/^lucide-/, ""));
        const allLucideIcons = await import("lucide-react") as any
        const LucideIcon = allLucideIcons[importName];
        if (!LucideIcon) {
          console.error(`could not find Lucide icon ${importName}, try one of the following or visit https://lucide.dev/icons`, allLucideIcons)
          return null;
        }

        return (
          <LucideIcon className={clsx("svg-inline--fa", props.className)}
                      style={{ width: props.size, height: props.size }}
                      color={props.color}
                      aria-label={props['aria-label'] ?? `${props.icon} icon`}
                      data-testid={props.icon} />
        )
      }

      if (props.icon.startsWith("radix-")) {
        const importName = kebabToCamelCase(props.icon.replace(/^radix-/, "")) + "Icon"
        const allRadixIcons = await import("@radix-ui/react-icons") as any
        const RadixIcon = allRadixIcons[importName]
        if (!RadixIcon) {
          console.error(`could not find Radix icon ${importName}, try one of the following or visit https://www.radix-ui.com/icons`, allRadixIcons)
          return null
        }
        return <RadixIcon className={clsx("svg-inline--fa", props.className)}
                          style={style}
                          height={undefined} width={undefined}
                          aria-label={props['aria-label'] ?? `${props.icon} icon`}
                          data-testid={props.icon} /> // remove hardcoded 15px from radix
      }

      if (props.icon.startsWith("mui-")) {
        const importName = kebabToCamelCase(props.icon.replace(/^mui-/, ""))
        const allMuiIcons = await import(`@mui/icons-material`) as any
        const MuiIcon = allMuiIcons[importName]
        if (!MuiIcon) {
          console.error(`could not find MUI icon ${importName}, try one of the following or visit https://mui.com/material-ui/material-icons/`, allMuiIcons)
          return null
        }

        // Extra classes to make consistent with other icons (otherwise it does not respect font size)
        return <MuiIcon className={clsx("svg-inline--fa", props.className)}
                        style={style}
                        fontSize="inherit"
                        aria-label={props['aria-label'] ?? `${props.icon} icon`}
                        data-testid={props.icon} />
      }

      if (props.prefixFallback) {
        return <DynamicIcon {...props} icon={props.prefixFallback + props.icon} />
      }

      return null
    }
  })

  return loadedIcon.isSuccess ? loadedIcon.data : null
}


function kebabToCamelCase(kebabText: string): string {
  return kebabText
    .split('-')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join('')
}
